
import React, {useState, useEffect, useMemo, useRef} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {useAuth} from '../hooks/useAuth';
import {useEvent} from '../hooks/useEvents';
import {useEventStatus} from '../hooks/useEventStatus';
import {useUser} from '../hooks/useUser';
import {useDarkMode} from '../hooks/useDarkMode';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AuthRequired from '../components/common/AuthRequired';
import EventHeader from '../components/event/EventHeader';
import UserSelector from '../components/event/UserSelector';
import UserWelcome from '../components/event/UserWelcome';
import ExpenseSection from '../components/event/ExpenseSection';
import CopyNotification from '../components/event/CopyNotification';
import {EventMeta, Expense} from '../types';
import EditEventModal from "../components/event/EditEventModal";
import {getEventSelection, removeEventSelection} from '../utils/eventSelections';
import {resolveParticipantNames} from '../utils/helpers';
import {useEventUsersCache} from '../hooks/useEventUsersCache';
import IndividualBalancesTab from '../components/summary/IndividualBalancesTab';
import SuggestedPaymentsTab from '../components/summary/SuggestedPaymentsTab';
import EventTabs from "@/components/event/EventTabs.tsx";
import AddEntrySheet from '../components/event/AddEntrySheet';
import MobileActionBar, { MobileAction } from '../components/common/MobileActionBar';
import { faPlus, faCoins, faHandHoldingDollar } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useEventHistory } from '../hooks/useEventHistory';
import { shareEventLink } from '../utils/shareEvent';

const EventPage: React.FC = () => {
    const {eventId} = useParams<{ eventId: string }>();
    const [searchParams] = useSearchParams();
    const {user, loading: authLoading} = useAuth();
    const [showEditModal, setShowEditModal] = useState(false);
    const {event, loading, error, updateEvent, removeParticipant, updateParticipantName} = useEvent(eventId);
    const {isClosed: isEventClosed, loading: statusLoading, closeEvent, reopenEvent} = useEventStatus(eventId);
    const {initializeUser} = useUser();
    const {eventUsers} = useEventUsersCache(eventId);
    const {darkMode, toggleDarkMode} = useDarkMode();
    const { t } = useTranslation();
    const { addToHistory } = useEventHistory();

    const preselectedUser = searchParams.get('user');
    const [selectedParticipant, setSelectedParticipant] = useState(preselectedUser || '');
    const [activeTab, setActiveTab] = useState<'expenses' | 'balances' | 'payments'>('expenses');
    const [copied, setCopied] = useState(false);
    // The phone's stand-in for the desktop form column — see AddEntrySheet.
    const [showAddSheet, setShowAddSheet] = useState(false);

    // Initialize user data when component mounts
    useEffect(() => {
        initializeUser(user);
    }, [user, initializeUser]);

    useEffect(() => {
        if (!eventId || preselectedUser || selectedParticipant) return;

        const storedSelection = getEventSelection(eventId);
        if (storedSelection) {
            setSelectedParticipant(storedSelection);
        }
    }, [eventId, preselectedUser, selectedParticipant]);

    // Auto-save event to history when it loads.
    // Keyed by user too: Firebase restores the session after the first render, so
    // an early visit would otherwise be recorded as anonymous and never re-saved
    // to the account once sign-in resolves.
    const savedEventRef = useRef<string | null>(null);
    useEffect(() => {
        if (authLoading || !event || !eventId || !event.meta.name) return;

        const saveKey = `${eventId}:${user?.uid || 'anonymous'}`;
        if (savedEventRef.current === saveKey) return;
        savedEventRef.current = saveKey;

        const pCount = Array.isArray(event.meta.participants)
            ? event.meta.participants.length
            : Object.keys(event.meta.participants || {}).length;
        addToHistory(eventId, event.meta.name, event.meta.description || '', pCount, event.meta.createdAt);
    }, [event, eventId, addToHistory, authLoading, user?.uid]);

    // Derived state
    const expenses = event ?
        (Object.values(event.expenses || {}) as Expense[])
            .sort((a, b) => b.timestamp - a.timestamp)
        : [];

    const isEventOwner = useMemo(() => {
        if (!event) return false;

        // Check if logged in user is the event owner
        if (user) {
            return event.meta.createdBy === user.uid;
        }

        return false;
    }, [user, event]);

    // Names shown throughout the bill. eventUsers wins over meta.participants:
    // a participant who is not the event owner can only write the former.
    const participants = useMemo(
        () => resolveParticipantNames(event?.meta.participants, eventUsers),
        [event?.meta.participants, eventUsers]
    );

    // Get selected participant display name
    const selectedParticipantName = selectedParticipant
        ? (participants[selectedParticipant] || selectedParticipant)
        : '';

    // Event handlers
    const handleCloseEvent = async () => {
        if (window.confirm(t('event.confirmations.close'))) {
            try {
                await closeEvent();
            } catch (error) {
                console.error('Error closing event:', error);
                alert(t('event.confirmations.closeError'));
            }
        }
    };

    const handleReopenEvent = async () => {
        if (window.confirm(t('event.confirmations.reopen'))) {
            try {
                await reopenEvent();
            } catch (error) {
                console.error('Error reopening event:', error);
                alert(t('event.confirmations.reopenError'));
            }
        }
    };

    const handleUpdateEvent = async (updates: Partial<EventMeta>) => {
        await updateEvent(eventId || '', updates);
    };

    const handleShareEvent = async () => {
        try {
            await shareEventLink(eventId || '', event?.meta.name);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // User cancelled share — do nothing
        }
    };

    const handleRemoveParticipant = async (participantId: string): Promise<boolean> => {
        if (!event || !eventId || !removeParticipant) return false;

        try {
            const success = await removeParticipant(eventId, participantId);
            if (success) {
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error removing participant:', error);
            alert(t('event.editEvent.form.participantRemoval.error'));
            return false;
        }
    };

    const handleUpdateParticipantName = async (participantId: string, newName: string): Promise<boolean> => {
        if (!event || !eventId || !updateParticipantName) return false;
        try {
            await updateParticipantName(eventId, participantId, newName);
            return true;
        } catch (error) {
            console.error('Error updating participant name:', error);
            return false;
        }
    };

    const handleSwitchUser = () => {
        if (eventId) {
            removeEventSelection(eventId);
        }

        // Reset selectedParticipant to show UserSelector
        setSelectedParticipant('');
    };

    // A tab switch from the bottom bar also returns to the top of the page: the
    // bar is only reached after scrolling down, and landing mid-way through a
    // freshly-swapped panel reads as a broken jump. It also puts EventTabs back
    // on screen, which is the way back to whichever tab was left.
    const showTab = (tab: 'expenses' | 'balances' | 'payments') => {
        setActiveTab(tab);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Add goes last: it is the button pressed over and over while entering a run
    // of bills, and the right end is where a right thumb already rests.
    const mobileActions: MobileAction[] = [
        {
            key: 'balances',
            label: t('event.tabs.balances'),
            icon: faCoins,
            active: activeTab === 'balances',
            onClick: () => showTab('balances'),
        },
        {
            key: 'payments',
            label: t('event.tabs.payments'),
            icon: faHandHoldingDollar,
            active: activeTab === 'payments',
            onClick: () => showTab('payments'),
        },
        ...(isEventClosed ? [] : [{
            key: 'add',
            label: t('event.expenseSection.addExpense'),
            icon: faPlus,
            primary: true,
            onClick: () => setShowAddSheet(true),
        }]),
    ];

    // Loading state
    if (loading) {
        return <LoadingSpinner />;
    }

    // Error state
    if (error || !event) {
        return <AuthRequired message={t('event.error.notFound')}/>;
    }

    const {meta} = event;

    return (
        <div className="glass-page-bg">
            <EventHeader
                meta={meta}
                isEventClosed={isEventClosed}
                statusLoading={statusLoading}
                darkMode={darkMode}
                onToggleDarkMode={toggleDarkMode}
            />

            {/* pb leaves room for the bottom action bar, which is fixed and would
                otherwise cover the last row of the list */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-28 lg:pb-6">
                {/* User Selection */}
                {!selectedParticipant && (
                    <UserSelector
                        participants={participants}
                        selectedParticipant={selectedParticipant}
                        onSelectParticipant={setSelectedParticipant}
                        eventId={eventId}
                        ownerId={meta.createdBy}
                    />
                )}

                {selectedParticipant && (
                    <>
                        {/* User Welcome with Edit Event button */}
                        <div className="mb-6">
                            <UserWelcome
                                selectedParticipant={selectedParticipantName}
                                selectedParticipantId={selectedParticipant}
                                eventId={eventId}
                                isEventClosed={isEventClosed}
                                copied={copied}
                                onSwitchUser={handleSwitchUser}
                                onShareEvent={handleShareEvent}
                                onEditEvent={() => setShowEditModal(true)}
                                onCloseEvent={handleCloseEvent}
                                onReopenEvent={handleReopenEvent}
                                loggedInUser={user?.uid || null}
                                eventOwnerId={meta.createdBy}
                            />
                        </div>

                        {/* Event Tabs */}
                        <EventTabs
                            activeTab={activeTab}
                            expenseCount={expenses.length}
                            onTabChange={setActiveTab}
                        />

                        {/* Tab Content */}
                        {activeTab === 'expenses' && (
                            <ExpenseSection
                                expenses={expenses}
                                participants={participants}
                                selectedParticipantId={selectedParticipant}
                                isEventClosed={isEventClosed}
                                eventId={eventId!}
                                eventOwnerId={meta.createdBy}
                                meta={meta}
                                settlementStatus={event.settlementStatus || {}}
                                settlementConfig={event.settlementConfig}
                            />
                        )}

                        {activeTab === 'balances' && (
                            <IndividualBalancesTab
                                expenses={expenses}
                                participants={participants}
                                eventId={eventId!}
                                eventName={meta.name}
                                shareRatios={meta.shareRatios}
                            />
                        )}

                        {activeTab === 'payments' && (
                            <SuggestedPaymentsTab
                                expenses={expenses}
                                participants={participants}
                                eventId={eventId!}
                                eventName={meta.name}
                                selectedParticipant={selectedParticipant}
                                isClosed={isEventClosed}
                                settlementStatus={event.settlementStatus || {}}
                                settlementConfig={event.settlementConfig}
                                shareRatios={meta.shareRatios}
                                isOwner={isEventOwner}
                                onCloseEvent={closeEvent}
                            />
                        )}

                        <CopyNotification show={copied}/>
                    </>
                )}
            </div>

            {/* Quick actions, phone only — the add form and the two summary tabs
                stay a thumb away once the list has been scrolled */}
            {selectedParticipant && <MobileActionBar actions={mobileActions} />}

            {/* Add sheet, phone only in practice: the bar that opens it is hidden
                from `lg` up, where the form has its own column */}
            {selectedParticipant && eventId && (
                <AddEntrySheet
                    open={showAddSheet}
                    onClose={() => setShowAddSheet(false)}
                    eventId={eventId}
                    participants={participants}
                    currentUser={selectedParticipant}
                    expenses={expenses}
                    shareRatios={meta.shareRatios}
                />
            )}

            {/* Edit Event Modal */}
            {showEditModal && eventId && (
                <EditEventModal
                    event={event.meta}
                    eventId={eventId}
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSave={handleUpdateEvent}
                    onRemoveParticipant={handleRemoveParticipant}
                    onUpdateParticipantName={handleUpdateParticipantName}
                />
            )}
        </div>
    );
};

export default EventPage;